<script lang="ts">
  import SingleBubbleIcon from '$icons/messages/SingleBubbleIcon.svelte';
  import { client } from '$lib/api/Client';
  import { toastCustom } from '$utils/toasts';
  import Button from '$lib/components/reusable/Button.svelte';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { projectCommentsQueryKey } from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import { ClientResponseError } from '$lib/api/ClientResponseError';
  import type { ListProjectCommentsResponse } from '$shared/api';
  import RichTextArea from '../../RichTextArea.svelte';
  import { generateTextFromTipTapContent, type TiptapContent } from '$shared/util';
  import { browser } from '$app/environment';
  import type { Editor } from '@tiptap/core';
  import { goto } from '$app/navigation'; // Import goto for navigation
  import UserIcon from '$icons/UserIcon.svelte';
  import ArrowRight from '$icons/arrows/ArrowRight.svelte';
  import type { HackathonProject, ProjectComment } from '$shared/lib';
  import type { User } from '$routes/(app)/+layout.server';

  export let project: HackathonProject;
  export let currentUser: User | null;
  export let onCommentSuccess: () => void;

  let editor: Editor;
  let body: TiptapContent = {
    type: 'doc',
    content: [],
  };

  const queryClient = useQueryClient();
  const createCommentMutation = createMutation({
    mutationFn: async () => {
      return client.projects.createComment({
        projectId: project.id,
        body: body,
      });
    },
    onMutate: async () => {
      if (!currentUser) return;

      await queryClient.cancelQueries({
        queryKey: projectCommentsQueryKey(project.id),
      });

      const previousData = queryClient.getQueryData<{
        pages: ListProjectCommentsResponse[];
      }>(projectCommentsQueryKey(project.id));

      if (previousData?.pages[0]) {
        // Create optimistic comment
        const newComment: ProjectComment = {
          id: Math.floor(Math.random() * 1000000),
          projectId: project.id,
          body: body,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isDeleted: false,
          likedByCurrentUser: false,
          likeCount: 0,
          replyCount: 0,
          hasMoreReplies: false,
          user: {
            id: currentUser.userId ?? 0,
            username: currentUser.username ?? '',
            displayName: currentUser.displayName ?? '',
            avatarUrl: currentUser.avatarUrl ?? '',
            accountRoles: [],
            batches: [],
          },
          replies: [],
        };

        // Add the new comment to the beginning of the first page
        queryClient.setQueryData(projectCommentsQueryKey(project.id), {
          ...previousData,
          pages: [
            {
              ...previousData.pages[0],
              comments: [newComment, ...previousData.pages[0].comments],
            },
            ...previousData.pages.slice(1),
          ],
        });
      }

      return { previousData };
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(projectCommentsQueryKey(project.id), context.previousData);
      }

      if (error instanceof ClientResponseError) {
        toastCustom({
          title: 'Error',
          description: error.message,
          negative: true,
          position: 'bottom-center',
        });
      } else {
        toastCustom({
          title: 'Error',
          description: 'An unknown error occurred while creating the comment.',
          negative: true,
          position: 'bottom-center',
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: projectCommentsQueryKey(project.id),
      });
    },
    onSuccess: () => {
      body = {
        type: 'doc',
        content: [],
      };
      editor?.chain().clearContent().run();
      onCommentSuccess();
    },
  });

  async function createComment() {
    await $createCommentMutation.mutateAsync();
    editor?.commands.clearContent();
  }

  function signIn() {
    goto('/signin'); // Navigate to the login page
  }

  $: isBodyEmpty = generateTextFromTipTapContent(body).length === 0;
</script>

<div class="bg-gray-dark-1 -mt-4 flex flex-col gap-y-4 rounded-md p-4">
  <div class="flex items-center gap-x-2">
    <SingleBubbleIcon class="text-gray-dark-11" />
    <h2 class="text-base">Add comment</h2>
  </div>
  {#if !currentUser}
    <div class="bg-gray-dark-2 flex flex-col items-center gap-y-4 rounded-md p-4 py-8 shadow-md">
      <UserIcon class="text-gray-dark-11 h-8 w-8" />
      <p class="text-gray-dark-11 w-1/2 text-center">
        You must be logged in to like or comment. If you don't have an account, you can register
        now.
      </p>
      <Button primary on:click={signIn}>
        Log In or Register <ArrowRight class="h-4 w-4" />
      </Button>
    </div>
  {:else if browser}
    <RichTextArea allowCodeBlock={false} bind:value={body} bind:editor />
  {/if}
  {#if currentUser}
    <div class="flex items-center justify-end">
      <Button
        secondary
        disabled={isBodyEmpty || $createCommentMutation.isPending}
        on:click={createComment}
        loading={$createCommentMutation.isPending}
      >
        Comment
      </Button>
    </div>
  {/if}
</div>
